import React, { useState, useRef } from 'react';
import { Upload, X, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { importGeoJSON, GeoJSONImportResponse } from '../lib/api';

interface GeoJSONImportProps {
    onImportComplete: (result: GeoJSONImportResponse) => void;
    onClose: () => void;
}

const GeoJSONImport: React.FC<GeoJSONImportProps> = ({ onImportComplete, onClose }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<GeoJSONImportResponse | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFile = async (file: File) => {
        if (!file.name.endsWith('.json') && !file.name.endsWith('.geojson')) {
            setError('Filen skal være en .json eller .geojson fil');
            return;
        }

        setIsImporting(true);
        setError(null);
        setImportResult(null);

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target?.result as string;
                const geoJson = JSON.parse(text);

                // Validate GeoJSON structure
                if (geoJson.type !== 'FeatureCollection') {
                    throw new Error('GeoJSON skal være en FeatureCollection');
                }

                if (!Array.isArray(geoJson.features)) {
                    throw new Error('GeoJSON features skal være et array');
                }

                // Import via API
                const result = await importGeoJSON(geoJson);
                setImportResult(result);
                onImportComplete(result);
            } catch (err: any) {
                setError(err.message || 'Fejl ved import af GeoJSON fil');
            } finally {
                setIsImporting(false);
            }
        };

        reader.onerror = () => {
            setError('Fejl ved læsning af fil');
            setIsImporting(false);
        };

        reader.readAsText(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFile(files[0]);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Import GeoJSON</h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            <div className="space-y-4">
                {/* File Upload Area */}
                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        isDragging
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                    } ${isImporting ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleClick}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json,.geojson"
                        onChange={handleFileInputChange}
                        className="hidden"
                    />
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-2">
                        Træk en GeoJSON fil hertil eller klik for at vælge
                    </p>
                    <p className="text-xs text-gray-500">
                        Understøttede formater: .json, .geojson
                    </p>
                </div>

                {/* Loading State */}
                {isImporting && (
                    <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Importerer...</span>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">Fejl</p>
                            <p className="text-sm text-red-600 mt-1">{error}</p>
                        </div>
                    </div>
                )}

                {/* Import Result */}
                {importResult && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-green-800">Import gennemført</p>
                                <div className="mt-2 space-y-1 text-sm text-green-700">
                                    <p>Oprettet: {importResult.created} pladser</p>
                                    {importResult.errors > 0 && (
                                        <p className="text-red-600">Fejl: {importResult.errors} pladser</p>
                                    )}
                                </div>

                                {/* Created Lots */}
                                {importResult.createdLots.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-xs font-medium text-green-800 mb-1">Oprettede pladser:</p>
                                        <ul className="text-xs text-green-700 space-y-1">
                                            {importResult.createdLots.map((lot, index) => (
                                                <li key={index} className="flex items-center">
                                                    <FileText className="h-3 w-3 mr-1" />
                                                    {lot.name} ({lot.lotId})
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Errors */}
                                {importResult.errors > 0 && importResult.errorDetails && importResult.errorDetails.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-xs font-medium text-red-800 mb-1">Fejl:</p>
                                        <ul className="text-xs text-red-700 space-y-1">
                                            {importResult.errorDetails.map((err, index) => (
                                                <li key={index}>
                                                    {err.lotId ? `${err.lotId}: ` : ''}{err.error}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-xs text-blue-800 font-medium mb-2">GeoJSON Format:</p>
                    <pre className="text-xs text-blue-700 bg-blue-100 p-2 rounded overflow-x-auto">
{`{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "geometry": {
        "type": "Polygon",
        "coordinates": [[[lng, lat], ...]]
      },
      "properties": {
        "id": "aal_100",
        "name": "Plads Navn",
        "address": "Adresse",
        "parking_type": "free",
        ...
      }
    }
  ]
}`}
                    </pre>
                </div>
            </div>
        </div>
    );
};

export default GeoJSONImport;

